import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Slider } from '@/components/ui/slider';
import { useToast } from '@/hooks/use-toast';
import { useEnhancedSessionGeneration } from '@/hooks/useEnhancedSessionGeneration';
import { useVoices, VoiceOption } from "@/hooks/useVoices";
import { useBackgroundMusicOptions, getBackgroundMusicUrl } from "@/hooks/useBackgroundMusic";
import { categoryConfigs } from '@/config/categories';
import { backendClient } from '@/lib/backendClient';
import EnhancedAudioPlayer from '@/components/EnhancedAudioPlayer';
import { Play, Share2, Video, Clock, Zap, Users, Volume2, Music, Mic, Check } from 'lucide-react';

interface ShortsTemplate {
  id: string;
  name: string;
  prompt: string;
  category: string;
  hook: string;
  recommendedVoice?: string;
  recommendedMusic?: string;
}

// All available voices will be fetched from the hook

// Generate dynamic templates based on categories
const generateCategoryTemplates = (): ShortsTemplate[] => {
  const baseTemplates: ShortsTemplate[] = [];
  
  // Add templates for each category
  Object.entries(categoryConfigs).forEach(([categoryId, config]) => {
    if (!config.name) return;
    
    const categoryName = config.name;
    const hooks = [
      `Transform your ${categoryName.toLowerCase()} in under a minute...`,
      `Discover the power of ${categoryName.toLowerCase()}...`,
      `Quick ${categoryName.toLowerCase()} boost in 30 seconds...`,
      `Experience instant ${categoryName.toLowerCase()} relief...`
    ];
    
    const hook = hooks[Math.floor(Math.random() * hooks.length)];
    
    baseTemplates.push({
      id: `${categoryId}-hook`,
      name: `${categoryName} Hook`,
      category: categoryId,
      hook,
      prompt: `Create a compelling ${categoryName.toLowerCase()} micro-session. Start with "${hook}" and provide one powerful technique. End with "Experience more with VoiceThera."`,
      recommendedMusic: categoryId === 'motivation' ? 'stomp-motivation.mp3' : 
                       categoryId === 'sleep' ? 'sounds_of_nature.mp3' : 
                       'universal_background.mp3',
      recommendedVoice: categoryId === 'motivation' ? 'male_coach' : 
                       categoryId === 'sleep' ? 'female_whisspy' : 
                       categoryId === 'massage' ? 'female_sleepish' : undefined
    });
  });
  
  // Add some premium templates
  baseTemplates.push(
    {
      id: 'brand-intro',
      name: 'VoiceThera Introduction',
      category: 'meditation',
      hook: 'Meet VoiceThera, your AI wellness companion...',
      prompt: 'Create a VoiceThera brand introduction. Start with "Meet VoiceThera, your AI wellness companion" and explain our personalized AI sessions, voice options, and how we help with anxiety, sleep, motivation, and more. End with "Start your wellness journey today."',
      recommendedVoice: 'female_default',
      recommendedMusic: 'universal_background.mp3'
    },
    {
      id: 'testimonial-hook',
      name: 'User Success Story',
      category: 'motivation',
      hook: 'Here\'s how VoiceThera changed my life...',
      prompt: 'Create a compelling user testimonial short. Start with "Here\'s how VoiceThera changed my life" and share a brief success story about overcoming anxiety, improving sleep, or finding motivation. End with "What will your success story be?"',
      recommendedVoice: 'male_alexander',
      recommendedMusic: 'stomp-motivation.mp3'
    }
  );
  
  return baseTemplates;
};

const SHORT_DURATIONS = [
  { value: '15 seconds', label: '15 seconds', icon: '⚡' },
  { value: '30 seconds', label: '30 seconds', icon: '🎯' },
  { value: '45 seconds', label: '45 seconds', icon: '⭐' },
  { value: '1 minute', label: '1 minute', icon: '💫' }
];

// Available categories for quick selection
const AVAILABLE_CATEGORIES = Object.entries(categoryConfigs).map(([id, config]) => ({
  id,
  name: config.name || id,
  description: config.description || `${config.name || id} sessions`
}));

const AdminShortsGenerator: React.FC = () => {
  const { voices } = useVoices();
  const allVoices = voices ? [...voices.male, ...voices.female] : [];
  const { options: backgroundMusicOptions } = useBackgroundMusicOptions();

  const [selectedDuration, setSelectedDuration] = useState<string>('30 seconds');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [customPrompt, setCustomPrompt] = useState<string>('');
  const [selectedVoice, setSelectedVoice] = useState<string>('female_default');
  const [selectedCategory, setSelectedCategory] = useState<string>('meditation');
  const [selectedMusic, setSelectedMusic] = useState<string>('universal_background.mp3');
  const [currentPreviewMusic, setCurrentPreviewMusic] = useState<string>('universal_background.mp3'); // freeze music for current preview
  const [musicVolume, setMusicVolume] = useState<number>(30);
  const [previewingVoice, setPreviewingVoice] = useState<string | null>(null);
  const [previewingMusic, setPreviewingMusic] = useState<string | null>(null);
  const [currentMusicAudio, setCurrentMusicAudio] = useState<HTMLAudioElement | null>(null);
  const [currentVoiceAudio, setCurrentVoiceAudio] = useState<HTMLAudioElement | null>(null);
  const [shortsTemplates] = useState<ShortsTemplate[]>(generateCategoryTemplates());
  const [templateRecommendedMusic, setTemplateRecommendedMusic] = useState<string | undefined>(undefined);
  const [generatedShorts, setGeneratedShorts] = useState<Array<{ 
    id: string; 
    duration: string; 
    audioUrl: string; 
    content: string; 
    template: string;
    voice: string;
    music: string; // display name
    musicFile: string; // file id used for playback
    category: string;
  }>>([]);

  const { toast } = useToast();
  const { 
    generateAudioFromPrompt, 
    isGenerating, 
    audioUrl, 
    generatedContent,
    resetAudio,
    currentStep 
  } = useEnhancedSessionGeneration();

  const handleTemplateSelect = (templateId: string) => {
    console.log('🎵 Template selected:', templateId);
    const template = shortsTemplates.find(t => t.id === templateId);
    if (!template) {
      console.log('🎵 Template not found for ID:', templateId);
      return;
    }

    setSelectedTemplate(templateId);
    setCustomPrompt(template.prompt);

    if (template.category) setSelectedCategory(template.category);
    if (template.recommendedVoice) setSelectedVoice(template.recommendedVoice);

    // Do NOT override user-selected music. Store recommendation for optional apply.
    setTemplateRecommendedMusic(template.recommendedMusic);
    if (template.recommendedMusic && template.recommendedMusic !== selectedMusic) {
      console.log('🎵 Template recommends music:', template.recommendedMusic, '— keeping current selection:', selectedMusic);
    }
  };

  const handleMusicChange = (musicId: string) => {
    console.log('🎵 DEBUG AdminShortsGenerator - Music selection changed to:', musicId);
    setSelectedMusic(musicId);
  };

  const playVoicePreview = async (voiceId: string) => {
    // Stop any currently playing voice
    if (currentVoiceAudio) {
      currentVoiceAudio.pause();
      setCurrentVoiceAudio(null);
    }

    if (previewingVoice === voiceId) {
      setPreviewingVoice(null);
      return;
    }

    try {
      setPreviewingVoice(voiceId);
      const voice = allVoices.find(v => v.id === voiceId);
      
      // Simple predefined voice samples - immediate playback like music preview
      const voiceSamples: Record<string, string> = {
        'female_default': '/voice-samples/sophia-preview.mp3',
        'female_secondary': '/voice-samples/isabella-preview.mp3', 
        'female_coachess': '/voice-samples/coachess-preview.mp3',
        'female_whisspy': '/voice-samples/whisspy-preview.mp3',
        'female_sleepish': '/voice-samples/sleepish-preview.mp3',
        'female_custom': '/voice-samples/michele-preview.mp3',
        'male_default': '/voice-samples/marcus-preview.mp3',
        'male_secondary': '/voice-samples/george-preview.mp3',
        'male_alexander': '/voice-samples/alexander-preview.mp3',
        'male_mike': '/voice-samples/mike-preview.mp3',
        'male_coach': '/voice-samples/coach-preview.mp3'
      };

      const sampleUrl = voiceSamples[voiceId];
      if (sampleUrl) {
        const audio = new Audio(sampleUrl);
        setCurrentVoiceAudio(audio);
        
        audio.addEventListener('ended', () => {
          setPreviewingVoice(null);
          setCurrentVoiceAudio(null);
        });
        
        audio.onerror = () => {
          console.log(`Voice sample file not found: ${sampleUrl}, using text preview`);
          setPreviewingVoice(null);
          toast({
            title: "Voice Preview",
            description: `${voice?.name || 'Voice'} sample not available. Voice will work in final generation.`,
            variant: "default"
          });
        };
        
        audio.play();
      } else {
        setPreviewingVoice(null);
        toast({
          title: "Voice Preview",
          description: `${voice?.name || 'Voice'} preview not available. Voice will work in final generation.`,
          variant: "default"
        });
      }
    } catch (error) {
      console.error('Voice preview failed:', error);
      setPreviewingVoice(null);
    }
  };

  const playMusicPreview = async (musicId: string) => {
    // Stop any currently playing music
    if (currentMusicAudio) {
      currentMusicAudio.pause();
      setCurrentMusicAudio(null);
    }

    if (previewingMusic === musicId) {
      setPreviewingMusic(null);
      return;
    }

    try {
      setPreviewingMusic(musicId);
      const { data } = await backendClient.storage
        .from('audio-sessions')
        .getPublicUrl(`backgrounds/${musicId}`);

      if (data?.publicUrl) {
        const audio = new Audio(data.publicUrl);
        audio.volume = musicVolume / 100;
        
        // Store reference to current audio
        setCurrentMusicAudio(audio);
        
        audio.addEventListener('ended', () => {
          setPreviewingMusic(null);
          setCurrentMusicAudio(null);
        });
        
        audio.onerror = () => {
          console.error(`Background music file not found: ${musicId}`);
          setPreviewingMusic(null);
          setCurrentMusicAudio(null);
          toast({
            title: "Music Preview Failed",
            description: `Background music "${musicId}" not found.`,
            variant: "destructive",
          });
        };

        audio.play();
        
        // Auto-stop after 10 seconds
        setTimeout(() => {
          if (audio && !audio.paused) {
            audio.pause();
            setPreviewingMusic(null);
            setCurrentMusicAudio(null);
          }
        }, 10000);
      }
    } catch (error) {
      console.error('Music preview failed:', error);
      setPreviewingMusic(null);
      setCurrentMusicAudio(null);
    }
  };

  const generateShort = async () => {
    if (!customPrompt.trim()) {
      toast({
        title: "Missing Prompt",
        description: "Please select a template or enter a custom prompt.",
        variant: "destructive",
      });
      return;
    }

    try {
      const selectedVoiceData = allVoices.find(v => v.id === selectedVoice);
      const selectedMusicData = backgroundMusicOptions.find(m => m.id === selectedMusic);
      
      console.log('🎵 DEBUG AdminShortsGenerator - Selected music state:', selectedMusic);
      console.log('🎵 DEBUG AdminShortsGenerator - Selected music data:', selectedMusicData);
      
      // Freeze current preview music to avoid later UI changes affecting playback
      setCurrentPreviewMusic(selectedMusic);
      console.log('🎵 DEBUG AdminShortsGenerator - currentPreviewMusic set to:', selectedMusic);
      
      const finalPrompt = `${customPrompt}\n\nSession Length: ${selectedDuration}\nVoice: ${selectedVoiceData?.name || 'Default'}\nBackground Music: ${selectedMusicData?.name || 'Ambient'}\nCategory: ${selectedCategory}\nOptimized for social media sharing.`;
      
      await generateAudioFromPrompt(
        finalPrompt,
        false, // useExactText
        false, // includeBiography
        '', // userBiography
        selectedCategory, // categoryId
        selectedVoiceData?.id.includes('male') ? 'male' : 'female', // voiceGender
        `Social Short - ${selectedDuration}`, // sessionTitle
        'standard', // voiceType
        'shorts', // sessionType
        undefined, // latencyTolerance
        undefined, // groupSession
        false, // usePersonalBiography
        undefined, // practitionerClient
        selectedVoice, // specific_voice_id - pass the actual voice ID
        undefined, // practitionerProfile
        false, // fastMode
        selectedMusic // backgroundMusicFile - pass the selected background music
      );

      toast({
        title: "Short Generated!",
        description: `${selectedDuration} social media short created successfully.`,
      });

    } catch (error: any) {
      toast({
        title: "Generation Failed",
        description: error.message || "Failed to generate short.",
        variant: "destructive",
      });
    }
  };

  const saveGenerated = () => {
    if (audioUrl && generatedContent) {
      const selectedVoiceData = allVoices.find(v => v.id === selectedVoice);
      const selectedMusicData = backgroundMusicOptions.find(m => m.id === selectedMusic);
      
      const newShort = {
        id: Date.now().toString(),
        duration: selectedDuration,
        audioUrl,
        content: generatedContent,
        template: selectedTemplate || 'custom',
        voice: selectedVoiceData?.name || 'Default',
        music: selectedMusicData?.name || 'Ambient', // display name
        musicFile: selectedMusic, // actual file id for playback
        category: selectedCategory
      };
      setGeneratedShorts(prev => [newShort, ...prev]);
      resetAudio();
      setCustomPrompt('');
      setSelectedTemplate('');
      
      toast({
        title: "Short Saved",
        description: "Short added to your collection.",
      });
    }
  };

  const shareAudio = async (audioUrl: string) => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'VoiceThera Short',
          text: 'Check out this meditation short from VoiceThera',
          url: audioUrl,
        });
      } else {
        await navigator.clipboard.writeText(audioUrl);
        toast({
          title: "Link Copied",
          description: "Audio URL copied to clipboard for sharing.",
        });
      }
    } catch (error) {
      console.error('Share failed:', error);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <Video className="h-6 w-6 text-primary" />
        <div>
          <h2 className="text-2xl font-bold">Social Media Shorts Generator</h2>
          <p className="text-muted-foreground">Create engaging 15s-1min audio content for TikTok & Instagram</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Generator Controls */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5" />
                Quick Setup
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Duration Selection */}
              <div>
                <label className="text-sm font-medium mb-2 block">Duration</label>
                <div className="grid grid-cols-4 gap-2">
                  {SHORT_DURATIONS.map((duration) => (
                    <Button
                      key={duration.value}
                      variant={selectedDuration === duration.value ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedDuration(duration.value)}
                      className="flex items-center gap-1"
                    >
                      <span>{duration.icon}</span>
                      {duration.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Category Selection */}
              <div>
                <label className="text-sm font-medium mb-2 block">Category</label>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AVAILABLE_CATEGORIES.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Voice Selection */}
              <div>
                <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                  <Mic className="h-4 w-4" />
                  Voice Selection
                </label>
                <div className="space-y-2">
                  <Select value={selectedVoice} onValueChange={setSelectedVoice}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <div className="px-2 py-1 text-xs font-medium text-muted-foreground">Male Voices</div>
                      {maleVoices.map((voice) => (
                        <SelectItem key={voice.id} value={voice.id}>
                          <div className="flex flex-col">
                            <span className="font-medium">{voice.name}</span>
                            <span className="text-xs text-muted-foreground">{voice.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                      <div className="px-2 py-1 text-xs font-medium text-muted-foreground">Female Voices</div>
                      {FEmaleVoices.map((voice) => (
                        <SelectItem key={voice.id} value={voice.id}>
                          <div className="flex flex-col">
                            <span className="font-medium">{voice.name}</span>
                            <span className="text-xs text-muted-foreground">{voice.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => playVoicePreview(selectedVoice)}
                    disabled={previewingVoice === selectedVoice}
                    className="w-full"
                  >
                    {previewingVoice === selectedVoice ? (
                        <div className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary"></div>
                          Playing Preview...
                        </div>
                    ) : (
                      <>
                        <Play className="h-3 w-3 mr-1" />
                        Preview Voice
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Background Music Selection */}
              <div>
                <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                  <Music className="h-4 w-4" />
                  Background Music
                </label>
                <div className="space-y-3">
                  <Select value={selectedMusic} onValueChange={handleMusicChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {backgroundMusicOptions.map((music) => (
                        <SelectItem key={music.id} value={music.id}>
                          <div className="flex flex-col">
                            <span className="font-medium">{music.name}</span>
                            <span className="text-xs text-muted-foreground">{music.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {templateRecommendedMusic && templateRecommendedMusic !== selectedMusic && (
                    <div className="flex items-center justify-between rounded-md border p-2 mt-2">
                      <div className="text-xs">
                        Template recommends: <span className="font-medium">
                          {backgroundMusicOptions.find(m => m.id === templateRecommendedMusic)?.name || templateRecommendedMusic}
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          if (templateRecommendedMusic) setSelectedMusic(templateRecommendedMusic);
                        }}
                      >
                        Apply
                      </Button>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => playMusicPreview(selectedMusic)}
                      disabled={previewingMusic === selectedMusic}
                      className="flex-1"
                    >
                      {previewingMusic === selectedMusic ? (
                        <>
                          <div className="animate-pulse rounded-full h-3 w-3 bg-primary mr-1"></div>
                          Playing...
                        </>
                      ) : (
                        <>
                          <Play className="h-3 w-3 mr-1" />
                          Preview
                        </>
                      )}
                    </Button>
                  </div>

                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block flex items-center gap-1">
                      <Volume2 className="h-3 w-3" />
                      Background Volume: {musicVolume}%
                    </label>
                    <Slider
                      value={[musicVolume]}
                      onValueChange={(value) => setMusicVolume(value[0])}
                      max={100}
                      step={5}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Content Templates</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                {shortsTemplates.slice(0, 20).map((template) => (
                  <Button
                    key={template.id}
                    variant={selectedTemplate === template.id ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleTemplateSelect(template.id)}
                    className="h-auto p-3 flex flex-col items-start text-left"
                  >
                    <div className="font-medium">{template.name}</div>
                    <div className="text-xs text-muted-foreground line-clamp-2">{template.hook}</div>
                    <div className="flex gap-1 mt-1">
                      <Badge variant="secondary" className="text-xs">{template.category}</Badge>
                      {template.recommendedVoice && (
                        <Badge variant="outline" className="text-xs">
                          {allVoices.find(v => v.id === template.recommendedVoice)?.name}
                        </Badge>
                      )}
                    </div>
                  </Button>
                ))}
              </div>

              <Separator />

              <div>
                <label className="text-sm font-medium mb-2 block">Custom Prompt</label>
                <Textarea
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  placeholder="Enter your custom prompt for the short..."
                  rows={4}
                />
              </div>

              <Button 
                onClick={generateShort} 
                disabled={isGenerating || !customPrompt.trim()}
                className="w-full"
                size="lg"
              >
                {isGenerating ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    {currentStep === 'content-generation' && 'Generating Content...'}
                    {currentStep === 'audio-synthesis' && 'Creating Audio...'}
                    {currentStep === 'voice-validation' && 'Validating Voice...'}
                    {!currentStep && 'Generating...'}
                  </div>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Generate Short
                  </>
                )}
              </Button>

              {/* Generated Result */}
              {(audioUrl || generatedContent) && (
                <Card className="bg-muted/50">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <Badge variant="secondary">{selectedDuration}</Badge>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={saveGenerated}>Save</Button>
                        {audioUrl && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => shareAudio(audioUrl)}
                          >
                            <Share2 className="h-3 w-3 mr-1" />
                            Share
                          </Button>
                        )}
                      </div>
                    </div>
                    {audioUrl && (
                      <EnhancedAudioPlayer
                        audioUrl={audioUrl}
                        backgroundAudioUrl={currentPreviewMusic ? getBackgroundMusicUrl(currentPreviewMusic) : undefined}
                      />
                    )}
                    {generatedContent && (
                      <div className="text-sm bg-background p-3 rounded border">
                        <strong>Content:</strong>
                        <p className="mt-1">{generatedContent}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Generated Shorts Collection */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Generated Shorts ({generatedShorts.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {generatedShorts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Video className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  No shorts generated yet
                </div>
              ) : (
                generatedShorts.map((short) => (
                  <Card key={short.id} className="p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex flex-wrap gap-1">
                        <Badge variant="outline" className="text-xs">
                          <Clock className="h-3 w-3 mr-1" />
                          {short.duration}
                        </Badge>
                        <Badge variant="secondary" className="text-xs">{short.category}</Badge>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => shareAudio(short.audioUrl)}
                      >
                        <Share2 className="h-3 w-3" />
                      </Button>
                    </div>
                    <div className="text-xs text-muted-foreground mb-2">
                      Voice: {short.voice} • Music: {short.music}
                    </div>
                    <EnhancedAudioPlayer
                      audioUrl={short.audioUrl}
                      backgroundAudioUrl={short.musicFile ? getBackgroundMusicUrl(short.musicFile) : undefined}
                    />
                  </Card>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminShortsGenerator;